import { useEffect, useRef } from 'react';

import { InviteSegmentEvents } from '../constants';
import { useInviteTracking } from './useInviteTracking';

interface LabelsTracked {
  propertyId: string | number;
  error: boolean;
  success: boolean;
}

type AxiosErrorShape = {
  name?: string;
  response?: { status?: number };
  code?: string;
};

interface UseLabelsResolvedTrackingParams {
  propertyId: string | number | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  labelsSource: 'default' | 'custom';
  labelCount: number;
}

// Fires `LabelsResolved/Loaded` once per property per outcome; re-fires only when the outcome flips.
export const useTrackScreeningLabelsLoaded = ({
  propertyId,
  isLoading,
  isError,
  error,
  labelsSource,
  labelCount,
}: UseLabelsResolvedTrackingParams) => {
  const { trackInvite } = useInviteTracking();
  const labelsTrackedRef = useRef<LabelsTracked | null>(null);

  useEffect(() => {
    if (!propertyId || isLoading) return;

    const tracked = labelsTrackedRef.current;
    const isSameProperty = tracked?.propertyId === propertyId;

    if (isError) {
      if (isSameProperty && tracked?.error) return;
      labelsTrackedRef.current = {
        propertyId,
        error: true,
        success: (isSameProperty && tracked?.success) || false,
      };
      const axiosError = error as AxiosErrorShape | null | undefined;
      trackInvite(InviteSegmentEvents.LabelsResolved, {
        property_id: propertyId,
        resolved_successfully: false,
        labels_source: labelsSource,
        label_count: 0,
        error_type: axiosError?.name ?? 'unknown',
        http_status: axiosError?.response?.status,
        error_code: axiosError?.code,
      });
      return;
    }

    if (isSameProperty && tracked?.success) return;
    labelsTrackedRef.current = {
      propertyId,
      error: (isSameProperty && tracked?.error) || false,
      success: true,
    };
    trackInvite(InviteSegmentEvents.LabelsResolved, {
      property_id: propertyId,
      resolved_successfully: true,
      labels_source: labelsSource,
      label_count: labelCount,
    });
  }, [propertyId, isLoading, isError, error, labelsSource, labelCount, trackInvite]);
};
